import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Bot } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Subscriptions() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "Streaming", monthly_price: 0 });
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    const { data } = await api.get("/lifestyle/subscriptions");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name) return;
    try {
      await api.post("/lifestyle/subscriptions", { ...form, monthly_price: parseFloat(form.monthly_price) });
      toast.success("Subscription added");
      setForm({ name: "", category: "Streaming", monthly_price: 0 });
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Couldn't add");
    }
  };

  const remove = async (id) => {
    await api.delete(`/lifestyle/subscriptions/${id}`);
    load();
  };

  const runScan = async () => {
    if (items.length === 0) {
      toast.error("Add some subscriptions first");
      return;
    }
    setScanning(true);
    try {
      const { data } = await api.post("/ai/scan-subscriptions", {
        subscriptions: items.map((i) => ({ name: i.name, category: i.category, monthly_price: i.monthly_price })),
      });
      setScan(data);
    } catch (e) {
      toast.error("Couldn't scan");
    } finally {
      setScanning(false);
    }
  };

  const total = items.reduce((s, i) => s + i.monthly_price, 0);

  return (
    <div data-testid="page-subs">
      <PageHeader
        eyebrow="Subscription Scanner"
        title="Audit your subscriptions"
        subtitle="AI recommends what to keep, downgrade, cancel, or bundle."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="add-sub-btn"><Plus className="w-4 h-4 mr-2" /> Add subscription</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add subscription</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label className="mb-1.5 block">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Netflix" data-testid="sub-name" /></div>
                <div><Label className="mb-1.5 block">Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Streaming" /></div>
                <div><Label className="mb-1.5 block">Monthly price ($)</Label><Input type="number" step="0.01" value={form.monthly_price} onChange={(e) => setForm({ ...form, monthly_price: e.target.value })} data-testid="sub-price" /></div>
              </div>
              <DialogFooter><Button onClick={add} className="bg-emerald-700 hover:bg-emerald-800 text-white">Add</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="border-amber-100 shadow-none rounded-2xl gradient-amber mb-6">
        <CardContent className="p-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-amber-700 font-semibold">Total monthly spend</div>
            <div className="font-display font-bold text-3xl mt-1">{fmtMoney(total)}/mo</div>
            <div className="text-sm text-gray-700 mt-1">{fmtMoney(total * 12)} per year</div>
          </div>
          <Button onClick={runScan} disabled={scanning} className="bg-amber-600 hover:bg-amber-700 text-white rounded-full" data-testid="run-scan">
            <Bot className="w-4 h-4 mr-2" /> {scanning ? "Scanning…" : "AI scan & recommend"}
          </Button>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-gray-200 shadow-none rounded-2xl">
          <CardContent className="p-0">
            <div className="p-5 border-b border-gray-200 font-display font-bold">Your subscriptions</div>
            <div className="divide-y divide-gray-100">
              {items.map((s) => (
                <div key={s.id} className="px-5 py-3 flex items-center justify-between" data-testid={`sub-${s.id}`}>
                  <div>
                    <div className="font-semibold">{s.name}</div>
                    <div className="text-xs text-gray-500">{s.category}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold">{fmtMoney(s.monthly_price)}</span>
                    <button onClick={() => remove(s.id)} className="p-1.5 text-gray-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {items.length === 0 && <div className="px-5 py-10 text-center text-gray-500 text-sm">No subscriptions yet. Add a few to get AI recommendations.</div>}
            </div>
          </CardContent>
        </Card>

        <Card className="border-sky-100 shadow-none rounded-2xl gradient-sky">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Bot className="w-4 h-4 text-sky-700" />
              <div className="text-xs uppercase tracking-[0.18em] text-sky-700 font-semibold">AI recommendations</div>
            </div>
            {scan ? (
              <>
                <div className="space-y-3">
                  {(scan.recommendations || []).map((r, i) => (
                    <div key={i} className="bg-white rounded-xl p-4 border border-sky-100" data-testid={`reco-${i}`}>
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <div className="font-semibold">{r.name}</div>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{r.action}</span>
                      </div>
                      <p className="text-sm text-gray-600">{r.reason}</p>
                      {r.monthly_savings > 0 && <div className="text-xs mt-1 text-emerald-700 font-semibold">Save {fmtMoney(r.monthly_savings)}/mo</div>}
                    </div>
                  ))}
                </div>
                <div className="mt-4 bg-white rounded-xl p-4 border border-sky-100">
                  <div className="text-sm text-gray-700"><strong>Estimated total monthly savings:</strong> <span className="text-emerald-700 font-bold">{fmtMoney(scan.total_monthly_savings)}</span></div>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-700">Click "AI scan & recommend" to see personalized advice on your subscriptions.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
